import { ref, computed, readonly } from 'vue'

export interface KeywordSuggestion {
  text: string
  category: 'make' | 'model' | 'feature' | 'color' | 'bodyType' | 'fuelType' | 'synonym'
  confidence: number
  matchCount?: number
}

export interface KeywordSuggestionResponse {
  suggestions: KeywordSuggestion[]
  totalMatches: number
  source: 'driveagent' | 'fallback' | 'cached'
}

export function useKeywordSuggestions() {
  // State
  const suggestions = ref<KeywordSuggestion[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const lastSearchTerm = ref('')
  
  // Cache for suggestions to avoid repeated API calls
  const suggestionCache = new Map<string, KeywordSuggestionResponse>()
  
  // Get suggestions from API
  const fetchSuggestions = async (term: string, maxSuggestions = 8): Promise<KeywordSuggestionResponse> => {
    const trimmedTerm = term.trim()
    
    if (!trimmedTerm || trimmedTerm.length < 1) {
      return {
        suggestions: [],
        totalMatches: 0,
        source: 'fallback'
      }
    }
    
    // Check cache first
    const cacheKey = trimmedTerm.toLowerCase()
    if (suggestionCache.has(cacheKey)) {
      return suggestionCache.get(cacheKey)!
    }
    
    try {
      loading.value = true
      error.value = null
      
      // Make API call
      const response = await $fetch(`/api/keyword-suggestions?term=${encodeURIComponent(trimmedTerm)}&maxSuggestions=${maxSuggestions}`) as {
        success: boolean
        suggestions: KeywordSuggestion[]
        totalMatches: number
        source: string
        meta: any
      }
      
      if (response.success && response.suggestions) {
        const result: KeywordSuggestionResponse = {
          suggestions: response.suggestions,
          totalMatches: response.totalMatches,
          source: response.source as 'driveagent' | 'fallback' | 'cached'
        }
        
        // Cache the result
        suggestionCache.set(cacheKey, result)
        
        // Limit cache size
        if (suggestionCache.size > 50) {
          const firstKey = suggestionCache.keys().next().value
          if (firstKey) {
            suggestionCache.delete(firstKey)
          }
        }
        
        return result
      } else {
        throw new Error('Invalid response from suggestions API')
      }
      
    } catch (err: any) {
      console.error('[useKeywordSuggestions] API Error:', err)
      error.value = err.message || 'Failed to get suggestions'
      
      // Return fallback suggestions
      return getFallbackSuggestions(trimmedTerm, maxSuggestions)
    } finally {
      loading.value = false
    }
  }
  
  // Fallback suggestions using common automotive terms
  const getFallbackSuggestions = (term: string, maxSuggestions = 8): KeywordSuggestionResponse => {
    const suggestions: KeywordSuggestion[] = []
    const lowerTerm = term.toLowerCase()
    
    // Common automotive terms
    const commonTerms = [
      { text: 'SUV', category: 'bodyType' as const },
      { text: 'sedan', category: 'bodyType' as const },
      { text: 'hatchback', category: 'bodyType' as const },
      { text: 'wagon', category: 'bodyType' as const },
      { text: 'automatic', category: 'feature' as const },
      { text: 'manual', category: 'feature' as const },
      { text: 'petrol', category: 'fuelType' as const },
      { text: 'diesel', category: 'fuelType' as const },
      { text: 'hybrid', category: 'fuelType' as const },
      { text: '4WD', category: 'feature' as const },
      { text: 'AWD', category: 'feature' as const },
      { text: 'red', category: 'color' as const },
      { text: 'blue', category: 'color' as const },
      { text: 'white', category: 'color' as const },
      { text: 'black', category: 'color' as const },
      { text: 'Toyota', category: 'make' as const },
      { text: 'Honda', category: 'make' as const },
      { text: 'Mazda', category: 'make' as const },
      { text: 'Ford', category: 'make' as const },
      { text: 'Holden', category: 'make' as const }
    ]
    
    // Filter terms that match the search
    commonTerms.forEach(term_obj => {
      if (term_obj.text.toLowerCase().includes(lowerTerm)) {
        suggestions.push({
          text: term_obj.text,
          category: term_obj.category,
          confidence: term_obj.text.toLowerCase().startsWith(lowerTerm) ? 0.9 : 0.7
        })
      }
    })
    
    // Add common synonyms
    const synonyms = getCommonSynonyms(lowerTerm)
    synonyms.forEach(synonym => {
      suggestions.push({
        text: synonym,
        category: 'synonym',
        confidence: 0.6
      })
    })
    
    // Sort by confidence and limit
    const sortedSuggestions = suggestions
      .sort((a, b) => b.confidence - a.confidence)
      .slice(0, maxSuggestions)
    
    return {
      suggestions: sortedSuggestions,
      totalMatches: 145, // Default vehicle count
      source: 'fallback'
    }
  }
  
  // Common automotive synonyms
  const getCommonSynonyms = (term: string): string[] => {
    const synonymMap: Record<string, string[]> = {
      'suv': ['4WD', 'AWD', 'crossover'],
      '4wd': ['SUV', 'AWD', 'all-wheel drive'],
      'awd': ['4WD', 'SUV'],
      'wagon': ['estate', 'touring'],
      'hatch': ['hatchback'],
      'sedan': ['saloon'],
      'ute': ['pickup', 'truck'],
      'auto': ['automatic'],
      'manual': ['stick shift'],
      'petrol': ['gasoline'],
      'diesel': ['turbo diesel'],
      'hybrid': ['electric', 'eco'],
      'leather': ['premium interior'],
      'sunroof': ['moonroof'],
      'nav': ['navigation', 'GPS'],
      'bluetooth': ['hands-free'],
    }
    
    const results: string[] = []
    Object.entries(synonymMap).forEach(([key, synonyms]) => {
      if (key.includes(term) || term.includes(key)) {
        results.push(...synonyms)
      }
    })
    
    return results
  }
  
  // Debounced search function (simple timeout-based)
  let searchTimeout: NodeJS.Timeout | null = null
  const debouncedSearch = (term: string, maxSuggestions = 8) => {
    if (searchTimeout) {
      clearTimeout(searchTimeout)
    }
    
    searchTimeout = setTimeout(async () => {
      const trimmedTerm = String(term || '').trim()
      if (trimmedTerm === lastSearchTerm.value.trim()) {
        return // Avoid duplicate searches
      }
      
      lastSearchTerm.value = trimmedTerm
      
      if (trimmedTerm.length === 0) {
        suggestions.value = []
        return
      }
      
      try {
        const result = await fetchSuggestions(trimmedTerm, maxSuggestions)
        suggestions.value = result.suggestions
      } catch (err) {
        console.error('[useKeywordSuggestions] Search error:', err)
        suggestions.value = []
      }
    }, 300)
  }
  
  // Public API
  const getSuggestions = (term: string | any, maxSuggestions = 8) => {
    const termString = String(term || '').trim()
    if (termString) {
      debouncedSearch(termString, maxSuggestions)
    }
  }
  
  const clearSuggestions = () => {
    suggestions.value = []
    lastSearchTerm.value = ''
    error.value = null
  }
  
  const clearCache = () => {
    suggestionCache.clear()
  }
  
  return {
    // State
    suggestions: readonly(suggestions),
    loading: readonly(loading),
    error: readonly(error),
    
    // Methods
    getSuggestions,
    clearSuggestions,
    clearCache,
    
    // Computed
    hasResults: computed(() => suggestions.value.length > 0)
  }
}
